import { LitElement, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import pimsterStickyStoryWidgetConfig from './pimster-sticky-story-widget.config';

import {
  STORY_PREVIEW_DEFAULT_BORDER_COLOR,
  STORY_PREVIEW_DEFAULT_DISPLAY,
  STORY_PREVIEW_DEFAULT_ANIMATIONS,
  StoryPreviewDisplay,
  StoryPreviewAnimation,
  STORY_PREVIEW_DEFAULT_WITH_PLAY_ICON,
  STORY_PREVIEW_DEFAULT_WITH_RADIUS,
  STORY_PREVIEW_DEFAULT_WITH_TITLE,
} from '../pimster-story-preview/pimster-story-preview.constants';
import { trackImpression } from '../analytics/events/impression';

import { hasAutoplay } from '../pimster-story-preview/pimster-story-preview.utils';
import { CookieConsent, StickyPlacement, Widget } from '../enums';
import { InvalidArgumentError } from '../utils/errors';
import {
  CLOSE_ANIMATION_DURATION_IN_MS,
  PATH_CONFIG_KEY,
  STICKY_STORY_WIDGET_DEFAULT_PLACEMENT,
} from './pimster-sticky-story-widget.constants';
import { pimsterStickyStoryWidgetStyle } from './pimster-sticky-story-widget.style';
import { EMBED_SERVER_URL } from '../constants';
import { renderContent } from './pimster-sticky-story-widget.render';
import { trackClose } from '../analytics/events/close';
import { booleanConverter } from '../utils/converter';

const { name } = pimsterStickyStoryWidgetConfig;

@customElement(name)
class PimsterStickyStoryWidget extends LitElement {
  @property({ type: Array<StoryPreviewAnimation> })
  animations = STORY_PREVIEW_DEFAULT_ANIMATIONS;

  @property({ type: String })
  border = undefined;

  @property({ type: String })
  borderColor = STORY_PREVIEW_DEFAULT_BORDER_COLOR;

  @property({ type: String })
  company = '';

  @property({ type: `${CookieConsent}` })
  cookieConsent = CookieConsent.Ask;

  @property({ type: `${StoryPreviewDisplay}` })
  display = STORY_PREVIEW_DEFAULT_DISPLAY;

  @property({ type: String })
  font = undefined;

  @property({
    type: Boolean,
    converter: booleanConverter,
  })
  withPlayIcon = STORY_PREVIEW_DEFAULT_WITH_PLAY_ICON;

  @property({
    type: Boolean,
    converter: booleanConverter,
  })
  withRadius = STORY_PREVIEW_DEFAULT_WITH_RADIUS;

  @property({
    type: Boolean,
    converter: booleanConverter,
  })
  withTitle = STORY_PREVIEW_DEFAULT_WITH_TITLE;

  @property({ type: `${StickyPlacement}` })
  placement = STICKY_STORY_WIDGET_DEFAULT_PLACEMENT;

  @property({ type: String })
  product = '';

  @property({ type: Number })
  storyId = undefined;

  @property({ type: String })
  private iframeId: string = '';

  @query('iframe')
  iframe!: HTMLIFrameElement;

  @state()
  private isVisible = true;

  @state()
  private computedHeight: string = '';

  private embedSrc: string = '';

  static styles = pimsterStickyStoryWidgetStyle;

  private close() {
    this.isVisible = false;
    trackClose({
      widgetType: Widget.StickyStory,
      company: this.company,
      product: this.product,
      storyId: `${this.storyId}`,
      display: this.display,
      placement: this.placement,
      animations: this.animations,
    });
    setTimeout(() => this.remove(), CLOSE_ANIMATION_DURATION_IN_MS);
  }

  private _buildRessourcesPath() {
    if (!this.company) {
      throw new InvalidArgumentError(
        'Element is missing at least one required argument'
      );
    }

    const path = `/company/${this.company}/product/${this.product || 'default'}/story/${this.storyId}`;

    const config = {
      animations: this.animations,
      border: this.border,
      borderColor: this.borderColor,
      cookieConsent: this.cookieConsent,
      display: this.display,
      font: this.font,
      withPlayIcon: this.withPlayIcon,
      withRadius: this.withRadius,
      withTitle: this.withTitle,
    };

    const configParam = encodeURIComponent(JSON.stringify(config));

    return `${path}?${PATH_CONFIG_KEY}=${configParam}`;
  }

  // Lifecycle Methods
  async connectedCallback() {
    super.connectedCallback();
    this.setComputedHeight();
    this.embedSrc = `${EMBED_SERVER_URL}${this._buildRessourcesPath()}`;
  }

  protected firstUpdated() {
    trackImpression({
      widgetType: Widget.StickyStory,
      company: this.company,
      product: this.product,
      storyId: this.storyId,
      isAutoplay: hasAutoplay(this.animations),
    });
  }

  protected update(changedProperties: PropertyValues): void {
    this.embedSrc = `${EMBED_SERVER_URL}${this._buildRessourcesPath()}`;
    this.setComputedHeight();
    super.update(changedProperties);
  }

  private setComputedHeight() {
    this.computedHeight = window.getComputedStyle(this).height;
  }

  // Render Method
  render() {
    return renderContent({
      placement: this.placement,
      display: this.display,
      embedSrc: this.embedSrc,
      iframeId: this.iframeId,
      isVisible: this.isVisible,
      computedHeight: this.computedHeight,
      withTitle: this.withTitle,
      close: this.close,
    });
  }
}

export default PimsterStickyStoryWidget;
